import React from 'react';
import { Routes, Route, Link, useNavigate } from 'react-router-dom';
import { ErrorBoundary } from '@ohif/ui-next';

// Route Components
import Local from './Local';
import Debug from './Debug';
import NotFound from './NotFound';
import Login from './Login';
import MobileLogin from './MobileLogin';
import buildModeRoutes from './buildModeRoutes';
import PrivateRoute from './PrivateRoute';
import PropTypes from 'prop-types';
import MainWrap from './MainWrap/MainWrap';
import MobileMainWrap from './MainWrap/MobileMainWrap';
import publicUrl from '../utils/publicUrl';
import CompactMainWrap from './CompactMainWrap';
import { routerBase, routerBasename } from '../utils/publicUrl';
import { UserReport, UserReportPortal, ReportDetail, ShareReportDetail, AccessCode } from '@ohif/ui-next';
import { useAppConfig } from '@state';
import { history } from '../utils/history';

const NotFoundServer = ({
  message = 'Unable to query for studies at this time. Check your data source configuration or network connection',
}) => {
  return (
    <div className="text-foreground absolute flex h-full w-full items-center justify-center">
      <div>
        <h4>{message}</h4>
      </div>
    </div>
  );
};

NotFoundServer.propTypes = {
  message: PropTypes.string,
};

const NotFoundStudy = () => {
  const [appConfig] = useAppConfig();
  const { showStudyList } = appConfig;

  return (
    <div className="text-foreground absolute flex h-full w-full items-center justify-center">
      <div>
        <h4>
          当前检查记录无法正常打开. 请返回{' '}
          <Link
            className="text-primary-light"
            to={'/'}
          >
            工作列表
          </Link>{' '}
          重新选择检查打开
        </h4>
        {showStudyList && (
          <p className="mt-2">
            Return to the{' '}
            <Link
              className="text-primary-light"
              to="/"
            >
              study list
            </Link>{' '}
            to select a different study to view.
          </p>
        )}
      </div>
    </div>
  );
};

NotFoundStudy.propTypes = {
  message: PropTypes.string,
};

// TODO: Include "routes" debug route if dev build
const bakedInRoutes = [
  {
    path: `/notfoundserver`,
    children: NotFoundServer,
  },
  {
    path: `/notfoundstudy`,
    children: NotFoundStudy,
  },
  {
    path: `/debug`,
    children: Debug,
  },
  {
    path: `/local`,
    children: Local.bind(null, { modePath: '' }), // navigate to the worklist
  },
  {
    path: `/localbasic`,
    children: Local.bind(null, { modePath: 'viewer/dicomlocal' }),
  },
];

// NOT FOUND (404)
const notFoundRoute = { path: '*', children: NotFound };

const createRoutes = ({
  modes,
  dataSources,
  extensionManager,
  servicesManager,
  commandsManager,
  hotkeysManager,
  showStudyList,
  appConfigState,
}: withAppTypes) => {
  const routes =
    buildModeRoutes({
      modes,
      dataSources,
      extensionManager,
      servicesManager,
      commandsManager,
      hotkeysManager,
    }) || [];

  const { customizationService } = servicesManager.services;

  const path =
    routerBasename.length > 1 && routerBasename.endsWith('/')
      ? routerBasename.substring(0, routerBasename.length - 1)
      : routerBasename;

  const WorkListRoute = {
    path: '/worklist', // Changed from '/' to '/worklist'
    children:
      (appConfigState?.systemNameFlag === 'yyx' || appConfigState?.systemNameFlag === 'doctoryyx')
        ? NotFound
        : MainWrap,
    // path: publicUrl,
    // children: DataSourceWrapper,
    private: true,
  };
  const MobileWorkListRoute = {
    path: '/mobileworklist',
    children: MobileMainWrap,
    private: true,
  };
  const PACSCompactRoute = {
    path: '/pacscompact',
    children: CompactMainWrap,
    private: true,
  };
  const LoginRoute = {
    path: '/', // New Login route bound to '/'
    children:
      (appConfigState?.systemNameFlag === 'yyx' || appConfigState?.systemNameFlag === 'doctoryyx')
        ? NotFound
        : Login,
    private: false, // Assuming login page is public
  };
  const MobileLoginRoute = {
    path: '/mobilelogin',
    children: MobileLogin,
    private: false,
  };
  const UserReportRoute = {
    path: '/userreport',
    children: appConfigState?.systemNameFlag === 'doctoryyx' ? NotFound : UserReport,
    private: false,
  };
  const userReportPortalRoute = {
    path: '/userreportportal',
    children: UserReportPortal,
    private: false,
  };
  const reportDetail = {
    path: '/reportdetail',
    children: ReportDetail,
    private: false,
  };
  const shareReportDetail = {
    path: '/sharereportdetail',
    children: ShareReportDetail,
    private: false,
  };
  const accessCode = {
    path: '/accesscode',
    children: AccessCode,
    private: false,
  };

  const customRoutes = customizationService.getCustomization('routes.customRoutes');

  const allRoutes = [
    ...routes,
    ...(showStudyList ? [WorkListRoute, MobileWorkListRoute] : []),
    PACSCompactRoute,
    LoginRoute,
    MobileLoginRoute,
    UserReportRoute,
    userReportPortalRoute,
    reportDetail,
    shareReportDetail,
    accessCode,
    // This next line adds a route on / to allow loading from the route and redirecting to the public url
    // ...(publicUrl !== '/' && showStudyList ? [{ ...WorkListRoute, path: '/' }] : []),
    // ...(publicUrl !== '/' && showStudyList ? [{ ...WorkListRoute, path: publicUrl }] : []),
    ...(customRoutes?.routes || []),
    ...bakedInRoutes,
    customRoutes?.notFoundRoute || notFoundRoute,
  ];

  function RouteWithErrorBoundary({ route, ...rest }) {
    const [appConfig] = useAppConfig();
    const { showErrorDetails } = appConfig;

    history.navigate = useNavigate();

    // eslint-disable-next-line react/jsx-props-no-spreading
    return (
      <ErrorBoundary
        context={`Route ${route.path}`}
        showErrorDetails={showErrorDetails}
      >
        <route.children
          {...rest}
          {...route.props}
          route={route}
          servicesManager={servicesManager}
          extensionManager={extensionManager}
          hotkeysManager={hotkeysManager}
        />
      </ErrorBoundary>
    );
  }

  const { userAuthenticationService } = servicesManager.services;

  // All routes are private by default and then we let the user auth service
  // to check if it is enabled or not
  // Todo: I think we can remove the second public return below
  return (
    <Routes>
      {allRoutes.map((route, i) => {
        return route.private === true ? (
          <Route
            key={i}
            path={route.path}
            element={
              <PrivateRoute
                handleUnauthenticated={() => userAuthenticationService.handleUnauthenticated()}
              >
                <RouteWithErrorBoundary route={route} />
              </PrivateRoute>
            }
          ></Route>
        ) : (
          <Route
            key={i}
            path={route.path}
            element={<RouteWithErrorBoundary route={route} />}
          />
        );
      })}
    </Routes>
  );
};

export default createRoutes;
